import { BoxBufferGeometry, Clock, Euler, EventDispatcher, IcosahedronBufferGeometry, Matrix4, PlaneBufferGeometry, Quaternion, SphereBufferGeometry, Vector3 } from "three";
import { loadAmmo } from "./loadAmmo";

export class EditorAmmo extends EventDispatcher {
  constructor ({ vm, ctx }) {
    super()
    this.ctx = ctx
    this.vm = vm

    this.clock = new Clock()
    this.gravityConstant = 9.89
    this.margin = 0.05

    this.bodyMeshMap = new Map()

    this.waiter = this.setup()
  }
  wait () {
    return this.waiter
  }
  async setup () {
    this.Ammo = await loadAmmo()

    let { Ammo } = this

    this.temp = {
      direction: new Euler(),
      euler: new Euler(),
      scale: new Vector3(),
      position: new Vector3(),
      quaternion: new Quaternion()
    }

    this.ta = {
      velocity: new Ammo.btVector3(0, 0, 0),
      worldTransform: new Ammo.btTransform(),
      scale: new Ammo.btVector3( 0, 0, 0 ),
      position: new Ammo.btVector3( 0, 0, 0 ),
      zero: new Ammo.btVector3( 0, 0, 0 ),
      quaternion: new Ammo.btQuaternion( 0, 0, 0 ),
      angularVelocity: new Ammo.btVector3(0, 0, 0),
      angularFactor: new Ammo.btVector3(0, 0, 0)
    }

    this.setupPhysics()
    this.setupCollision()
  }

  getBodyMeshByUUID (key) {
    return this.bodyMeshMap.get(key)
  }

  setMeshPosition ({ mesh, local = false, alwaysUp = false }) {
    const { body } = this.bodyMeshMap.get(mesh.uuid)
    body.activate()

    // body.setAngularVelocity(this.ta.zero)
    // body.setLinearVelocity(this.ta.zero)
    // body.setAngularFactor(this.ta.zero)

    if (alwaysUp) {
      mesh.rotation.x = 0
      mesh.rotation.z = 0
      mesh.quaternion.setFromEuler(mesh.rotation)
      // mesh.rotation.y = 0
    }

    if (local) {
      this.temp.position.copy(mesh.position)
      this.temp.quaternion.copy(mesh.quaternion)
    } else {
      mesh.getWorldPosition(this.temp.position)
      mesh.getWorldQuaternion(this.temp.quaternion)
    }

    // this.temp.quaternion.set(0, 0, 0, 1.0)
    // mesh.getWorldScale(this.temp.scale)

    this.ta.worldTransform.setIdentity()

    this.ta.quaternion.setValue(this.temp.quaternion.x, this.temp.quaternion.y, this.temp.quaternion.z, this.temp.quaternion.w)
    this.ta.position.setValue(this.temp.position.x, this.temp.position.y, this.temp.position.z)
    this.ta.worldTransform.setOrigin( this.ta.position );
    this.ta.worldTransform.setRotation( this.ta.quaternion );
``
    body.setWorldTransform( this.ta.worldTransform );
  }

  getShapeFromSimpleGeo ({ mesh, geometry }) {
    let { Ammo, margin } = this
    const parameters = geometry.parameters;

    if (mesh && mesh.userData && mesh.userData.capsule) {
      const shape = new Ammo.btCapsuleShape(mesh.userData.capsule.x / 2, mesh.userData.capsule.y / 2 - mesh.userData.capsule.x / 2)
      shape.setMargin(margin)

      return shape
    } else if (geometry instanceof PlaneBufferGeometry) {
      const sx = parameters.width !== undefined ? parameters.width / 2 : 0.5;
      const sy = parameters.height !== undefined ? parameters.height / 2 : 0.5;
      const sz = 0.1;

      const shape = new Ammo.btBoxShape( new Ammo.btVector3( sx, sy, sz ) );
      shape.setMargin( margin );

      return shape;
    } else if (geometry instanceof BoxBufferGeometry) {
      const sx = parameters.width !== undefined ? parameters.width / 2 : 0.5;
      const sy = parameters.height !== undefined ? parameters.height / 2 : 0.5;
      const sz = parameters.depth !== undefined ? parameters.depth / 2 : 0.5;

      const shape = new Ammo.btBoxShape( new Ammo.btVector3( sx, sy, sz ) );
      shape.setMargin( margin );

      return shape;
    } else if (geometry instanceof SphereBufferGeometry || geometry instanceof IcosahedronBufferGeometry) {
      const radius = parameters.radius !== undefined ? parameters.radius : 1;

      const shape = new Ammo.btSphereShape( radius );
      shape.setMargin( margin );

      return shape;
    }

    return null;
  }

  removeMesh ({ mesh }) {
    let { body } = this.bodyMeshMap.get(mesh.uuid)
    this.dynamicsWorld.removeRigidBody(body)
    this.bodyMeshMap.delete(mesh.uuid)
  }

  addHullMesh ({ mesh, mass = 0.1, rootScale = 1 }) {
    let hullShape = this.getHullShapeByMesh({ mesh, rootScale })
    this.makeBodyByShapeThenAddWorld({ mesh, shape: hullShape, mass, rootScale })
  }

  getHullShapeByMesh ({ mesh, rootScale }) {
    let geometry = mesh.geometry
    let attributes = geometry.attributes
    let position = attributes.position
    let array = position.array

    // console.log(mesh.name, mesh.uuid)
    let target = new Vector3()
    geometry.computeBoundingBox()
    geometry.boundingBox.getCenter(target)

    let scale = mesh.scale
    let matrixWorld = mesh.matrixWorld

    let center = new Vector3()

    let { Ammo, margin } = this
    const originalHull = new Ammo.btConvexHullShape()
    originalHull.setMargin(margin)

    let inverse = new Matrix4()
    let transformM4 = new Matrix4()

    transformM4.identity()
    transformM4.makeTranslation(target.x, target.y, target.z)

    let btVertex = new Ammo.btVector3()
    const rawVertexData = array
    let vertex = new Vector3()
    for (let i = 0; i < rawVertexData.length; i += 3) {
      transformM4.multiplyMatrices(inverse, matrixWorld)
      vertex
        .set(rawVertexData[i], rawVertexData[i + 1], rawVertexData[i + 2])
        .applyMatrix4(transformM4)
        .sub(center)
      btVertex.setValue(vertex.x, vertex.y, vertex.z)
      originalHull.addPoint(btVertex, i === rawVertexData.length - 3)
    }

    originalHull.type = 'hull'
    originalHull.setMargin(margin)
    originalHull.destroy = () => {
      for (let res of originalHull.resources || []) {
        Ammo.destroy(res)
      }
      if (originalHull.heightfieldData) {
        Ammo._free(originalHull.heightfieldData)
      }
      Ammo.destroy(originalHull)
    }

    let localScale = new Ammo.btVector3(rootScale * scale.x, rootScale * scale.y, rootScale * scale.z)
    originalHull.setLocalScaling(localScale)
    Ammo.destroy(localScale)

    return originalHull
  }

  makeBodyByShapeThenAddWorld ({ mesh, shape, mass, rootScale }) {
    let { Ammo } = this

    let position = new Vector3()
    let quaternion = new Quaternion()
    let scale = new Vector3()
    let rotaion = new Euler()

    // new Mesh()
    mesh.getWorldPosition(position)
    mesh.getWorldQuaternion(quaternion)
    mesh.getWorldScale(scale)

    position.copy(mesh.position)
    quaternion.copy(mesh.quaternion)
    scale.copy(mesh.scale)
    rotaion.copy(mesh.rotation)

    let startTransform = new Ammo.btTransform();
    startTransform.setIdentity();
    startTransform.setOrigin(new Ammo.btVector3(position.x, position.y, position.z));
    startTransform.setRotation(new Ammo.btQuaternion(quaternion.x, quaternion.y, quaternion.z, quaternion.w))

    var localInertia = new Ammo.btVector3(0, 0, 0);
    shape.calculateLocalInertia(mass, localInertia);

    var myMotionState = new Ammo.btDefaultMotionState(startTransform);
    var rbInfo = new Ammo.btRigidBodyConstructionInfo(mass, myMotionState, shape, localInertia);
    var body = new Ammo.btRigidBody(rbInfo);

    body.uuid = mesh.uuid

    for (let kn in mesh.userData) {
      body[kn] = mesh.userData[kn]
    }

    const localScale = new Ammo.btVector3(rootScale * scale.x, rootScale * scale.y, rootScale * scale.z)
    // localScale.setValue(scale.x, scale.y, scale.z)
    shape.setLocalScaling(localScale)

    Ammo.destroy(localScale)
    Ammo.destroy(localInertia)
    Ammo.destroy(startTransform)

    this.dynamicsWorld.addRigidBody(body)
    this.bodyMeshMap.set(mesh.uuid, { body, mesh })
  }

  // PlaneBufferGeometry
  // BoxBufferGeometry
  // SphereBufferGeometry
  addSimpleMesh ({ mesh, mass = 0.1, rootScale = 1 }) {
    let shape = this.getShapeFromSimpleGeo({ mesh, geometry: mesh.geometry })

    if (!shape) {
      throw new Error('no shape detected')
    }

    this.makeBodyByShapeThenAddWorld({ mesh, shape, mass, rootScale })
  }

  // addCapsule ({ mesh, r = 1, h = 2, mass = 0.1, rootScale = 1 }) {
  //   let { Ammo } = this
  //   let shape = new Ammo.btCapsuleShape(r, h / 2)

  //   if (!shape) {
  //     throw new Error('no shape detected')
  //   }

  //   let position = new Vector3()
  //   let quaternion = new Quaternion()
  //   let scale = new Vector3()

  //   // new Mesh()
  //   mesh.getWorldPosition(position)
  //   mesh.getWorldQuaternion(quaternion)
  //   mesh.getWorldScale(scale)

  //   let startTransform = new Ammo.btTransform();
  //   startTransform.setIdentity();
  //   startTransform.setOrigin(new Ammo.btVector3(position.x, position.y, position.z));
  //   startTransform.setRotation(new Ammo.btQuaternion(quaternion.x, quaternion.y, quaternion.z, quaternion.w))

  //   var localInertia = new Ammo.btVector3(0, 0, 0);
  //   shape.calculateLocalInertia(mass, localInertia);

  //   var myMotionState = new Ammo.btDefaultMotionState(startTransform);
  //   var rbInfo = new Ammo.btRigidBodyConstructionInfo(mass, myMotionState, shape, localInertia);
  //   var body = new Ammo.btRigidBody(rbInfo);

  //   body.setDamping(0.95, 0.99)

  //   body.uuid = mesh.uuid

  //   for (let kn in mesh.userData) {
  //     body[kn] = mesh.userData[kn]
  //   }

  //   const localScale = new Ammo.btVector3(rootScale * scale.x, rootScale * scale.y, rootScale * scale.z)
  //   // localScale.setValue(scale.x, scale.y, scale.z)
  //   shape.setLocalScaling(localScale)

  //   Ammo.destroy(localScale)
  //   Ammo.destroy(localInertia)
  //   Ammo.destroy(startTransform)

  //   this.dynamicsWorld.addRigidBody(body)

  //   this.bodyMeshMap.set(mesh.uuid, { body, mesh })
  // }

  setupCollision () {
    let { Ammo, ctx } = this
    // let vec3 = new Ammo.btVector3()
    ctx.onLoop(() => {
      let dispatcher = this.dispatcher
      for (let i = 0, il = dispatcher.getNumManifolds(); i < il; i++) {
        const contactManifold = dispatcher.getManifoldByIndexInternal(i)
        // const key = Object.keys(contactManifold.getBody0())[0]

        const body0 = Ammo.castObject(contactManifold.getBody0(), Ammo.btRigidBody)
        const body1 = Ammo.castObject(contactManifold.getBody1(), Ammo.btRigidBody)

        if (!('useAmmoCollide' in body0 && 'useAmmoCollide' in body1)) {
          return
        }

        let objA = this.getBodyMeshByUUID(body0.uuid)
        let objB = this.getBodyMeshByUUID(body1.uuid)
        let objs = [
          objA,
          objB
        ]

        this.dispatchEvent({
          type: 'hit',
          objA,
          objB,
          objs,
          get: (typeName, typeKey = 'cType') => {
            return objs.find(e => e.mesh.userData[typeKey] === typeName)
          }
        })

        // do not check collision between 2 unnamed objects
        // (fragments do not have a name, for example)
        // if (body0.name === '' && body1.name === '') continue

        // let character = false
        // let halo = false
        // if ((body0.isChar && body1.isHalo)) {
        //   character = body0
        //   halo = body1
        // }
        // if ((body1.isChar && body0.isHalo)) {
        //   character = body1
        //   halo = body0
        // }
        // if (character && halo) {
        //   // console.log(character, halo)
        //   vec3.setValue(0, 2.5, 0)
        //   halo.applyCentralImpulse(vec3)
        //   this.dispatchEvent({
        //     type: 'halo',
        //     body: halo,
        //     uuid: halo.uuid
        //   })
        // }
      }
    })
  }

  setupPhysics () {
    let { Ammo } = this
    let collisionConfiguration = new Ammo.btDefaultCollisionConfiguration();
    let dispatcher = new Ammo.btCollisionDispatcher( collisionConfiguration );
    let broadphase = new Ammo.btDbvtBroadphase();
    let solver = new Ammo.btSequentialImpulseConstraintSolver();
    let dynamicsWorld = new Ammo.btDiscreteDynamicsWorld( dispatcher, broadphase, solver, collisionConfiguration );
    dynamicsWorld.setGravity( new Ammo.btVector3( 0, -this.gravityConstant, 0 ) );

    this.dynamicsWorld = dynamicsWorld
    this.dispatcher = dispatcher

    var transform = new Ammo.btTransform();

    this.ctx.onLoop(() => {
      var deltaTime = this.clock.getDelta();
      dynamicsWorld.stepSimulation(deltaTime, 7);

      let values = this.bodyMeshMap.values()
      for (let { body, mesh } of values) {

        body.getMotionState().getWorldTransform(transform)

        var origin = transform.getOrigin()

        let position = {
          x: origin.x(),
          y: origin.y(),
          z: origin.z()
        }

        var rotation = transform.getRotation();

        let quaternion = {
          x: rotation.x(),
          y: rotation.y(),
          z: rotation.z(),
          w: rotation.w()
        }

        mesh.position.copy(position)
        mesh.quaternion.copy(quaternion)
      }
    })
  }
}